// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useRef } from 'react';
import { Animated, Dimensions, Easing, Platform, Pressable, StyleSheet, View } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';

import { Text } from '@/components/Themed';
import { useI18n } from '@/i18n';
import TourLeaderboardScreen from '../../../tour/[tourId]/leaderboard';
import appColors, { alpha } from '@/theme/colors';

export default function TourLeaderboardSheetScreen() {
  const { t } = useI18n();
  const router = useRouter();
  const { tourId: tourIdParam } = useLocalSearchParams<{ tourId?: string }>();
  const tourId = typeof tourIdParam === 'string' ? tourIdParam : '';
  const windowHeight = Dimensions.get('window').height;
  const translateY = useRef(new Animated.Value(windowHeight)).current;

  useEffect(() => {
    Animated.timing(translateY, {
      toValue: 0,
      duration: 260,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: Platform.OS !== 'web',
    }).start();
  }, [translateY]);

  const closeSheet = useCallback(() => {
    const fallbackTarget = tourId ? `/tour/${tourId}/round-init` : '/my-tours';
    const currentPath =
      Platform.OS === 'web' && typeof window !== 'undefined'
        ? window.location.pathname
        : null;

    Animated.timing(translateY, {
      toValue: windowHeight,
      duration: 180,
      easing: Easing.in(Easing.cubic),
      useNativeDriver: Platform.OS !== 'web',
    }).start(() => {
      router.back();
    });

    if (Platform.OS === 'web' && typeof window !== 'undefined' && currentPath) {
      setTimeout(() => {
        if (window.location.pathname === currentPath) {
          window.location.assign(fallbackTarget);
        }
      }, 230);
    }
  }, [router, tourId, translateY, windowHeight]);

  return (
    <Pressable style={styles.overlay} onPress={closeSheet}>
      <Animated.View style={[styles.sheet, { transform: [{ translateY }] }]}>
        <View style={styles.header}>
          <Pressable onPress={closeSheet} hitSlop={8}>
            <Text style={styles.backText}>{t('menu.back')}</Text>
          </Pressable>
          <Text style={styles.title}>{t('tourLeaderboard.title')}</Text>
          <View style={styles.rightSpacer} />
        </View>
        <Pressable style={styles.content} onPress={() => null}>
          <TourLeaderboardScreen />
        </Pressable>
      </Animated.View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: alpha(appColors.surface.tintDeep, 0.14),
    justifyContent: 'flex-end',
  },
  sheet: {
    height: '88%',
    backgroundColor: appColors.background.panel,
    borderTopLeftRadius: 18,
    borderTopRightRadius: 18,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    overflow: 'hidden',
  },
  header: {
    height: 52,
    paddingHorizontal: 14,
    borderBottomWidth: 1,
    borderBottomColor: alpha(appColors.accent.glow, 0.22),
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: appColors.text.secondary,
  },
  backText: {
    color: appColors.text.primary,
    fontSize: 15,
    fontWeight: '700',
  },
  title: {
    color: appColors.text.primary,
    fontSize: 16,
    fontWeight: '700',
  },
  rightSpacer: {
    width: 56,
  },
  content: {
    flex: 1,
  },
});
